/**
 * SibaScoreBadge — the compact pill for grid cards and dense surfaces.
 *
 * The smallest of the three SibaScore rendering surfaces:
 *   `<SibaScoreBadge/>` — compact color pill (this file)
 *   `<SibaScoreGauge/>` — big animated semicircle (PDP hero)
 *   `<SibaScoreBar/>`   — segmented tier bar with marker (comparisons)
 *
 * All three read live config from `useSibaScoreConfig()` — NO hardcoded
 * tiers, colors or thresholds anywhere in the trio. Change the admin config
 * once → the whole platform picks it up next request.
 *
 * Two variants:
 *   variant="pill" (default) — "842 · Excellent" full pill (grid cards)
 *   variant="dot"            — tiny color dot + number only (chip strips)
 */
import React from "react";
import { useSibaScoreConfig, tierForScore } from "@/hooks/useSibaScoreRanges";

type Variant = "pill" | "dot";
type Size = "xs" | "sm" | "md";

interface Props {
  score: number | null | undefined;
  variant?: Variant;
  size?: Size;
  showLabel?: boolean;
  className?: string;
  /** Additional testid suffix so multiple badges on one page stay unique. */
  testIdSuffix?: string;
}

const PILL_SIZE_CLS: Record<Size, string> = {
  xs: "text-[10px] px-1.5 py-0.5 gap-1",
  sm: "text-[11px] px-2 py-0.5 gap-1",
  md: "text-xs px-2.5 py-1 gap-1.5",
};

const DOT_CLS: Record<Size, string> = {
  xs: "h-1.5 w-1.5",
  sm: "h-2 w-2",
  md: "h-2.5 w-2.5",
};

/** Convert a hex color to an rgba background at low alpha for pill fills. */
function _tint(hex: string, alpha = 0.14): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.replace("#", ""));
  if (!m) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export const SibaScoreBadge: React.FC<Props> = ({
  score,
  variant = "pill",
  size = "sm",
  showLabel = true,
  className = "",
  testIdSuffix,
}) => {
  const { ranges } = useSibaScoreConfig();
  if (score == null || Number.isNaN(score)) return null;

  const tier = tierForScore(score, ranges);
  const rounded = Math.round(score);
  const suffix = testIdSuffix ? `-${testIdSuffix}` : "";

  if (variant === "dot") {
    return (
      <span
        data-testid={`sibascore-badge-dot${suffix}`}
        data-score={rounded}
        data-tier={tier.label}
        className={`inline-flex items-center gap-1 font-medium tabular-nums ${className}`}
        title={`SibaScore ${rounded} · ${tier.label}`}
      >
        <span className={`rounded-full ${DOT_CLS[size]}`} style={{ backgroundColor: tier.color }} />
        <span style={{ color: tier.color }}>{rounded}</span>
      </span>
    );
  }

  return (
    <span
      data-testid={`sibascore-badge${suffix}`}
      data-score={rounded}
      data-tier={tier.label}
      className={`inline-flex items-center rounded-full font-medium tabular-nums whitespace-nowrap ${PILL_SIZE_CLS[size]} ${className}`}
      style={{
        backgroundColor: _tint(tier.color, 0.14),
        color: tier.color,
        boxShadow: `inset 0 0 0 1px ${_tint(tier.color, 0.28)}`,
      }}
    >
      <span className="font-bold">{rounded}</span>
      {showLabel && (
        <>
          <span className="opacity-40">·</span>
          <span className="uppercase tracking-wide text-[0.85em]">{tier.label}</span>
        </>
      )}
    </span>
  );
};

export default SibaScoreBadge;
